import Modal from '../../components/modal/modal'
import { Nullable } from '../../types/common'
import { checkMobileVersion } from '../../utils/check-media'
import { checkEscKey } from '../../utils/utils'
import Carousel from '../carousel/carousel'
import ZoomCursor from '../zoom-cursor/zoom-cursor'

class ProductModal extends Modal {
  selectors: any
  element: Nullable<HTMLElement>
  carousel: Nullable<Carousel>
  slides: HTMLElement[]
  zoomCursor: Nullable<ZoomCursor>
  selectedSlideNumber: Nullable<HTMLElement>
  slideNumber: Nullable<HTMLElement>
  mediaIndexMap: any
  isZoomed: boolean
  zoomedImage: any

  constructor() {
    super()

    this.selectors = {
      carousel: `[data-product-modal-carousel]`,
      carouselButtons: `[data-product-modal-carousel-button]`,
      carouselSlide: `[data-product-modal-slide]`,
      carouselSelectedSlideNumber: `[data-product-modal-selected-slide-number]`,
      carouselSlideNumber: `[data-product-modal-slide-number]`,
      zoomCursor: `zoom-cursor`,
    }

    if (this.element) {
      this.carousel = this.element.querySelector(this.selectors.carousel)
      this.carouselButtons = this.carousel.querySelectorAll(
        this.selectors.carouselButtons,
      )
      this.slides = [
        ...this.element.querySelectorAll(this.selectors.carouselSlide),
      ]
      this.selectedSlideNumber = this.element.querySelector(
        this.selectors.carouselSelectedSlideNumber,
      )
      this.slideNumber = this.element.querySelector(
        this.selectors.carouselSlideNumber,
      )
    }

    this.zoomCursor = document.querySelector(this.selectors.zoomCursor)

    this.mediaIndexMap = this.createMediaIndexMap()

    this.isZoomed = false
  }

  handleKeydown = (event: KeyboardEvent): void => {
    const key = event.key || event.keyCode

    const isEscape = checkEscKey(key)

    if (isEscape) {
      this.isZoomed ? this.zoomedImage.reset() : this.hide()
    }
  }

  handleCarouselInit = (): void => {
    this.updateCarouselDraggableState()
  }

  handleCarouselSelect = (): void => {
    this.updateSelectedSlideNumber()
  }

  openAndShowMediaById(mediaId) {
    this.show()

    if (!this.carousel) {
      return
    }

    const mediaIndex = this.mediaIndexMap[mediaId]

    this.carousel.embla.scrollTo(mediaIndex, true)

    if (this.carouselButtons[0]) {
      this.carouselButtons[0].focus()
    }

    this.updateSelectedSlideNumber()
  }

  initCarousel(): void {
    if (!this.carousel) {
      return
    }

    this.carousel.embla.on('select', this.handleCarouselSelect)
    this.carousel.embla.on('init', this.handleCarouselInit)
  }

  resetCarousel(): void {
    if (!this.carousel) {
      return
    }

    this.carousel.embla.off('select', this.handleCarouselSelect)
    this.carousel.embla.off('init', this.handleCarouselInit)
  }

  hide(): any {
    this.resetCarousel()

    super.hide()

    this.isZoomed = false

    this.updateModal()

    this.emit('hide', {})
  }

  show(): any {
    super.show()

    this.initCarousel()
  }

  updateSelectedSlideNumber() {
    if (!this.selectedSlideNumber || !this.carousel) {
      return
    }

    const selectedIndex = this.carousel.embla.selectedScrollSnap()
    const newSlideNumber = selectedIndex + 1

    this.selectedSlideNumber.textContent = newSlideNumber
  }

  updateModal(): void {
    this.updateCarouselDraggableState()
    this.updateZoomCursorState()
    this.updateZoomState()
  }

  createMediaIndexMap() {
    return this.slides.reduce((acc, slide, index) => {
      if (!slide.dataset.mediaId) {
        return acc
      }

      return { ...acc, [slide.dataset.mediaId]: index }
    }, {})
  }

  updateCarouselDraggableState() {
    if (!this.carousel) {
      return
    }

    const isMobile = checkMobileVersion()

    if (!isMobile) {
      this.carousel.reInit({ draggable: false })

      return
    }

    this.carousel.reInit({ draggable: !this.isZoomed })
  }

  updateZoomCursorState(): void {
    if (!this.zoomCursor) {
      return
    }

    this.zoomCursor.updateState(this.isZoomed)
  }

  updateZoomState(): void {
    this.toggleAttribute('zoom-enabled', this.isZoomed)
  }
}

export default ProductModal
